import { useEffect, useState } from "react";
import { Check, X, Ban, ShieldCheck } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { ACCESS_STATUS, SCOPE_LABEL } from "@/lib/dataAccess";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const dt = (s) => (s ? new Date(s).toLocaleString("tr-TR") : "-");

export default function OfficeDataAccessPage() {
  const [items, setItems] = useState([]);
  const [issued, setIssued] = useState(null);

  const load = async () => setItems((await api.get("/office/data-access/requests")).data);
  useEffect(() => { load(); }, []);

  const act = async (r, action) => {
    try {
      const { data } = await api.post(`/office/data-access/requests/${r.id}/${action}`);
      if (action === "approve") setIssued({ ...data, req: r });
      else toast.success(action === "reject" ? "Talep reddedildi" : "Erişim iptal edildi");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6" data-testid="office-data-access-page">
      <div>
        <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Gizlilik</div>
        <h1 className="text-4xl font-extrabold tracking-tight text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>Veri Erişim Talepleri</h1>
        <p className="text-slate-500 mt-1 max-w-2xl">Sistem yönetimi, ofisinizin kayıtlarını sizin onayınız olmadan göremez. Onay verdiğinizde tek kullanımlık bir kod oluşur; bu kodu yalnızca güvendiğiniz kanaldan iletin. İzni istediğiniz an iptal edebilirsiniz.</p>
      </div>

      <Card className="divide-y">
        {items.length === 0 && <div className="p-10 text-center text-slate-400">Talep yok.</div>}
        {items.map((r) => (
          <div key={r.id} data-testid={`office-access-row-${r.id}`} className="p-4 flex items-start justify-between gap-4">
            <div>
              <div className="font-semibold">{SCOPE_LABEL[r.scope]} · {r.duration_hours} saat</div>
              <div className="text-sm text-slate-600 mt-0.5">Gerekçe: {r.reason}</div>
              <div className="text-xs text-slate-500 mt-1">Talep eden: {r.requested_by_name} · {dt(r.created_at)} · Görüntüleme: {r.view_count}{r.last_viewed_at && ` (son: ${dt(r.last_viewed_at)})`}</div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Badge variant="outline" className={ACCESS_STATUS[r.status]?.cls}>{ACCESS_STATUS[r.status]?.label}</Badge>
              {r.status === "PENDING" && (
                <>
                  <Button size="sm" data-testid={`approve-access-${r.id}`} onClick={() => act(r, "approve")} className="bg-emerald-600 hover:bg-emerald-700"><Check className="w-3.5 h-3.5 mr-1" />Onayla</Button>
                  <Button size="sm" variant="outline" data-testid={`reject-access-${r.id}`} onClick={() => act(r, "reject")}><X className="w-3.5 h-3.5 mr-1" />Reddet</Button>
                </>
              )}
              {["APPROVED", "ACTIVE"].includes(r.status) && (
                <Button size="sm" variant="outline" data-testid={`revoke-access-${r.id}`} onClick={() => act(r, "revoke")}><Ban className="w-3.5 h-3.5 mr-1" />İptal Et</Button>
              )}
            </div>
          </div>
        ))}
      </Card>

      <Dialog open={!!issued} onOpenChange={(o) => !o && setIssued(null)}>
        <DialogContent className="max-w-sm text-center">
          <DialogHeader><DialogTitle className="flex items-center justify-center gap-2"><ShieldCheck className="w-5 h-5 text-emerald-600" />Tek Kullanımlık Erişim Kodu</DialogTitle></DialogHeader>
          <div data-testid="issued-access-code" className="font-mono text-4xl font-bold tracking-[0.3em] py-4">{issued?.code}</div>
          <p className="text-sm text-slate-600">Bu kod yalnızca bir kez gösterilir ve {dt(issued?.code_expires_at)} tarihine kadar geçerlidir. Yöneticiye güvenli bir kanaldan iletin.</p>
          <Button onClick={() => setIssued(null)} className="bg-slate-900">Tamam</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
